import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import { getSessionDetails } from '@/api/endpoints';
import type { SessionDetail } from '@/types/api.types';
import type { WorkspaceDocType, WorkspaceQuestion } from '../types';

/**
 * The stable, typed shape returned by {@link useWorkspaceSession}.
 */
export interface UseWorkspaceSessionResult {
  /** The session id under review, echoed back for convenience (null when idle). */
  sessionId: string | null;
  /** The raw session detail from the API, or undefined while loading / on error. */
  session: SessionDetail | undefined;
  /** Responses mapped to workspace questions, including the write-back anchor. */
  questions: WorkspaceQuestion[];
  /** Editor engine to mount, derived from the uploaded filename's extension. */
  docType: WorkspaceDocType;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Derive the editor doc type from the uploaded filename's extension.
 */
function deriveDocType(filename: string | undefined | null): WorkspaceDocType {
  const lower = (filename ?? '').toLowerCase();
  if (lower.endsWith('.docx')) return 'docx';
  if (lower.endsWith('.xlsx')) return 'xlsx';
  return 'other';
}

/**
 * Map a session detail's responses to {@link WorkspaceQuestion}s.
 *
 * Mirrors the field mapping in `useSession.ts` (so confidence, citations,
 * user action, user response, answer source, index and question text all line
 * up with the rest of the app) but additionally preserves the per-response
 * `anchor` that the workspace editor needs for click-to-jump navigation.
 */
function mapResponses(session: SessionDetail | undefined): WorkspaceQuestion[] {
  if (!session) return [];
  return session.responses.map((r) => ({
    index: r.question_index,
    result_id: null,
    question_id: r.response_id,
    incoming_question: r.question_text,
    response_tier: r.response_tier,
    confidence: r.confidence * 100, // API returns 0-1, frontend expects 0-100
    suggested_response: r.matched_answer ?? '',
    citations: r.citations ?? [],
    requires_review: r.requires_review,
    review_reason: r.review_reason,
    canonical_question_id: null,
    source_qualitative_id: null,
    answer_source: r.answer_source ?? null,
    userAction: mapUserAction(r.user_action),
    userResponse: r.modified_answer ?? undefined,
    modifiedBy: r.reviewed_by ?? undefined,
    modifiedDate: r.reviewed_at ?? undefined,
    anchor: r.anchor ?? null,
  }));
}

function mapUserAction(
  action: string | null,
): 'Pending' | 'Accepted' | 'Modified' | 'Rejected' {
  if (!action) return 'Pending';
  switch (action) {
    case 'Accept':
      return 'Accepted';
    case 'Modify':
      return 'Modified';
    case 'Reject':
      return 'Rejected';
    default:
      return 'Pending';
  }
}

/**
 * Load a session and shape it for the document workspace.
 *
 * Unlike the shared `useSessionLoader`, this hook keeps each response's
 * write-back `anchor` (needed by the editor to navigate to a question) and
 * derives the `docType` so the workspace can pick the right editor engine.
 * It does not hydrate any shared store, so it can be used in isolation.
 */
export function useWorkspaceSession(sessionId: string | null): UseWorkspaceSessionResult {
  const query = useQuery({
    queryKey: queryKeys.sessionDetails(sessionId ?? ''),
    queryFn: () => getSessionDetails(sessionId!),
    enabled: !!sessionId,
    staleTime: 30_000,
  });

  const session = query.data;
  const questions = useMemo(() => mapResponses(session), [session]);
  const docType = useMemo(() => deriveDocType(session?.uploaded_filename), [session]);

  return {
    sessionId,
    session,
    questions,
    docType,
    isLoading: query.isLoading,
    error: query.error,
  };
}
